import { forwardRef, useImperativeHandle, useState } from "react";
import { createUseStyles } from "react-jss";
import produce from "immer";
import SidebarLightbox from "../../refresh/SidebarLightbox";
import { image } from "../CreateHotspot";
import SidebarItem from "../../refresh/SidebarItem";
import styles from "./sharedStyles";
import GenericOptions from "../GenericOptions";
import Element from "../elements";
import Checkbox from "../../refresh/inputs/Checkbox";
import RedButton from "../../refresh/RedButton";
import ListButtons from "../../refresh/ListButtons";

const useStyles = createUseStyles({
  ...styles,
  element: {
    position: "relative",
    "min-height": 80,
    "box-sizing": "border-box",
    "padding-left": 35,
  },
});

// Media Hotspots consist of a series of elements
// which can be found in ../elements

const EditMediaHotspot = ({
  isLink,
  setLink,
  account,
  onComplete,
}, ref) => {
  const classes = useStyles();
  const [show, setShow] = useState(false);
  const [config, setConfig] = useState({
    options: {
      tag: false,
      elements: [],
    }
  });

  useImperativeHandle(ref, () => ({
    show: (data) => {
      setConfig(produce(data, draft => {
        draft.options.tag ??= false;
        draft.options.elements ??= [];
      }));
      setShow(true);
    },
    hide: () => setShow(false),
  }));

  const onClose = () => {
    setShow(false);
  }

  return (
    <SidebarLightbox
      show={show}
      onClose={onClose}
      sidebar={
        <>
          <h1 className={classes.title}>
            Edit Media Popup
          </h1>
          {!config.options.tag && (
            <>
              <p className={classes.subtitle}>
                Add new elements from the left, and edit them on the right
              </p>
              <SidebarItem
                image={image`info-plus`}
                title="Rich Text"
                subtitle="Display a block of rich text"
                onClick={() =>
                  setConfig(
                    produce(draft => {
                      draft.options.elements.push({
                        id: Date.now(),
                        type: "text",
                        format: "richtext",
                        content: "",
                      });
                    }),
                  )
                }
              />
              <SidebarItem
                image={image`info`}
                title="Plain Text Block"
                subtitle="Display an unstyled block of text"
                onClick={() =>
                  setConfig(
                    produce(draft => {
                      draft.options.elements.push({
                        id: Date.now(),
                        type: "text",
                        format: "plaintext",
                        content: "",
                      });
                    }),
                  )
                }
              />
              <SidebarItem
                image={image`gallery`}
                title="Image"
                subtitle="Embed an image"
                onClick={() =>
                  setConfig(
                    produce(draft => {
                      draft.options.elements.push({
                        id: Date.now(),
                        type: "image",
                      });
                    }),
                  )
                }
              />
              <SidebarItem
                image={image`web-link`}
                title="Embedded Link"
                subtitle="Embed external content"
                onClick={() =>
                  setConfig(
                    produce(draft => {
                      draft.options.elements.push({
                        id: Date.now(),
                        type: "link",
                      });
                    }),
                  )
                }
              />
              <SidebarItem
                image={image`video`}
                title="Video"
                subtitle="Play a video clip"
                onClick={() =>
                  setConfig(
                    produce(draft => {
                      draft.options.elements.push({
                        id: Date.now(),
                        type: "video",
                        video_type: "cms",
                      });
                    }),
                  )
                }
              />
              <SidebarItem
                image={image`info`}
                title="Document"
                subtitle="Embed a PDF document"
                onClick={() =>
                  setConfig(
                    produce(draft => {
                      draft.options.elements.push({
                        id: Date.now(),
                        type: "document",
                      });
                    }),
                  )
                }
              />
              <SidebarItem
                image={image`audio`}
                title="Audio"
                subtitle="Play an audio clip"
                onClick={() =>
                  setConfig(
                    produce(draft => {
                      draft.options.elements.push({
                        id: Date.now(),
                        type: "audio",
                      });
                    }),
                  )
                }
              />
            </>
          )}
          <div className={classes.grow} />
          <Checkbox
            sidebar
            value={config.options.tag}
            onChange={value =>
              setConfig(
                produce(draft => {
                  draft.options.tag = value;
                }),
              )
            }
          >
            Tag node (disable popup)
          </Checkbox>
          {!config.options.tag && (
            <>
              <Checkbox
                sidebar
                value={config.options.large}
                onChange={value =>
                  setConfig(
                    produce(draft => {
                      draft.options.large = value;
                    }),
                  )
                }
              >
                Fullscreen popup
              </Checkbox>
              <Checkbox sidebar value={isLink} onChange={setLink}>
                Show in Links menu
              </Checkbox>
            </>
          )}
        </>
      }
    >
      <div className={classes.options}>
        <GenericOptions
          icon="Note Plus"
          iconSet={[
            "Note",
            "Note Plus",
            "Video",
            "Audio",
            "Link",
            "Photo",
            "Tag",
          ]}
          config={config}
          setConfig={setConfig}
          account={account}
          template
          hideIcon
          title={!config.options.tag}
        />
        {!config.options.tag && (
          <>
            <hr />
            {config.options.elements.length > 0 ? (
              config.options.elements.map(({ id, type, ...rest }, i) => (
                <div key={id}>
                  <div className={classes.element}>
                    <ListButtons
                      list="elements"
                      index={i}
                      setConfig={setConfig}
                    />
                    <Element
                      type={type}
                      value={rest}
                      onChange={values =>
                        setConfig(
                          produce(draft => {
                            draft.options.elements[i] = { id, type, ...values };
                          }),
                        )
                      }
                    />
                  </div>
                  {config.options.elements.length - 1 !== i && <hr />}
                </div>
              ))
            ) : (
              <p>Build your popup by adding elements from the left</p>
            )}
          </>
        )}
        <RedButton
          disabled={
            !(config.tooltip && (config.options?.title || config.options.tag))
          }
          onClick={() => onComplete(config)}
        >
          Save
        </RedButton>
      </div>
    </SidebarLightbox>
  );
};

export default forwardRef(EditMediaHotspot);
